import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";
import config from "../../config";
import { addToCart } from "../../slices/cartSlice";
import { setPaymentData } from "../../slices/paymentSlice";
import "./workshopreg.css";
import "./loadingOverlay.css";

const WorkshopRegistration = () => {
  const dispatch = useDispatch();

  const authToken = useSelector((state) => state.auth.token);
  const deviceId = useSelector((state) => state.auth.deviceId);
  const eventId = useSelector((state) => state.auth.eventId);
  const reduxApplicantId = useSelector((state) => state.auth.applicantId);
  const userId = useSelector((state) => state.auth.userId) || "";

  const cartItems = useSelector((state) => state.cart.items);

  const applicantId = authToken ? reduxApplicantId || 0 : 0;

  const [workshops, setWorkshops] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchWorkshops = async () => {
      try {
        const url = config.API_WORKSHOP_LIST_URL.replace(
          "{EventID}",
          eventId
        ).replace("{ApplicantID}", applicantId);
        const response = await fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({}),
        });
        if (!response.ok) {
          throw new Error("Failed to fetch workshops");
        }
        const data = await response.json();
        // Add an extra property for seat availability.
        const updatedWorkshops = data.Table.map((w) => ({
          ...w,
          seatUnavailable: false,
        }));
        setWorkshops(updatedWorkshops);
      } catch (error) {
        console.error("Error fetching workshops:", error);
      }
    };

    if (eventId) {
      fetchWorkshops();
    }
  }, [eventId, applicantId]);

  const purchasedWorkshops = authToken
    ? workshops.filter(
        (w) => w.PaidStatus && w.PaidStatus.trim().toUpperCase() === "PAID"
      )
    : [];

  const alreadyAddedWorkshops = authToken
    ? workshops.filter(
        (w) => w.PaidStatus && w.PaidStatus.trim().toLowerCase() === "added"
      )
    : [];

  // For available workshops (for signed-in users, exclude purchased, API added, and cart items).
  const availableWorkshops = authToken
    ? workshops.filter((w) => {
        const status = w.PaidStatus ? w.PaidStatus.trim().toUpperCase() : "";
        const inCart = cartItems.find(
          (item) => item.WorkshopID === w.WorkshopID
        );
        return status !== "PAID" && status !== "ADDED" && !inCart;
      })
    : workshops;

  // Filter available workshops using the search term.
  const filteredAvailableWorkshops = availableWorkshops.filter((w) => {
    const term = searchTerm.toLowerCase();
    return (
      (w.Topic && w.Topic.toLowerCase().includes(term)) ||
      (w.theDate && w.theDate.toLowerCase().includes(term)) ||
      (w.Facilitators && w.Facilitators.toLowerCase().includes(term)) ||
      (w.Venue && w.Venue.toLowerCase().includes(term))
    );
  });

  // Check seat availability for a workshop.
  const checkSeatAvailability = async (workshop) => {
    if (!config.API_CHECK_AVAILABLE_SEAT_URL) {
      console.warn(
        "API_CHECK_AVAILABLE_SEAT_URL not defined in config. Skipping seat availability check."
      );
      return true;
    }
    const seatUrl = config.API_CHECK_AVAILABLE_SEAT_URL.replace(
      "{DeviceID}",
      deviceId
    )
      .replace("{UserID}", encodeURIComponent(userId))
      .replace("{workshopID}", workshop.WorkshopID);
    try {
      const res = await fetch(seatUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        },
      });
      const data = await res.json();
      // Expecting response: { Table: [ { Status: true } ] }
      return data.Table[0].Status === true;
    } catch (error) {
      console.error("Error checking seat availability:", error);
      return false;
    }
  };

  // Handler to add a workshop to the cart.
  const handleAddToCart = async (workshop) => {
    if (!authToken) {
      alert("Please sign in first to add this workshop to your cart.");
      return;
    }
    if (cartItems.find((item) => item.WorkshopID === workshop.WorkshopID)) {
      alert("This workshop is already added to your cart.");
      return;
    }
    const available = await checkSeatAvailability(workshop);
    if (!available) {
      alert("There are no seats available in this workshop.");
      setWorkshops((prev) =>
        prev.map((w) =>
          w.WorkshopID === workshop.WorkshopID
            ? { ...w, seatUnavailable: true }
            : w
        )
      );
      return;
    }
    dispatch(addToCart(workshop));
  };

  // Handler to proceed with payment for an already added workshop.
  const handleProceedForAdded = async (workshop) => {
    if (!workshop || !workshop.PaymentCode) {
      alert("Payment code not available for this workshop.");
      return;
    }
    setLoading(true);
    const paymentPayload = {
      orderID: workshop.PaymentCode,
      returnPath: "https://i2.sihs.org.pk/paymentconfirm.aspx",
      OrderName: "SMDC",
      OrderInfo: "International Conference/Workshops/CPD/Events Registrations",
    };
    try {
      const initiateResponse = await axios.post(
        config.API_INITIATE_PAYMENT_URL,
        paymentPayload,
        {
          headers: { "Content-Type": "application/json" },
        }
      );
      console.log("Initiate Payment Response:", initiateResponse.data);
      if (
        initiateResponse.data.Transaction &&
        initiateResponse.data.Transaction.PaymentPortal
      ) {
        dispatch(
          setPaymentData({
            transactionID: initiateResponse.data.Transaction.TransactionID,
            paymentPortal: initiateResponse.data.Transaction.PaymentPortal,
          })
        );
        window.location.href = initiateResponse.data.Transaction.PaymentPortal;
      } else {
        alert("Payment registration failed.");
        setLoading(false);
      }
    } catch (error) {
      console.error(
        "Error during payment initiation for added workshop:",
        error
      );
      alert("An error occurred during payment initiation. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className="workshop-registration-container">
      {loading && (
        <div className="loading-overlay">
          <div className="spinner"></div>
          <p>Please wait...</p>
        </div>
      )}
      <header className="workshop-header">
        <h1>Conference Workshop Schedule</h1>
      </header>
      <div className="search-container">
        <input
          type="text"
          placeholder="Search by topic, date, facilitators, or venue..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {/* Purchased Workshops Section */}
      {authToken && purchasedWorkshops.length > 0 && (
        <div className="section-container">
          <h2>Purchased Workshops</h2>
          <div className="workshop-list-reg">
            {purchasedWorkshops.map((workshop, index) => (
              <div
                key={`${workshop.WorkshopID}-${index}`}
                className="workshop-card"
              >
                <h3 className="workshop-title">{workshop.Topic}</h3>
                <p className="workshop-date">
                  <strong>{workshop.theDate}</strong>
                </p>
                <p className="workshop-price">
                  <strong>RS: {workshop.Price}</strong>
                </p>
                <p className="workshop-facilitators">
                  <strong>{workshop.Facilitators}</strong>
                </p>
                <p className="workshop-facilitators">
                  <strong>{workshop.Venue}</strong>
                </p>
                <p className="workshop-facilitators">
                  <strong>{workshop.duration}</strong>
                </p>
                <p className="workshop-focal">
                  <strong>{workshop.Focal}</strong>
                </p>
                <span className="already-registered">Purchased</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Already Added (Pending Payment) Section */}
      {authToken && alreadyAddedWorkshops.length > 0 && (
        <div className="section-container">
          <h2>Added to Order (Pending Payment)</h2>
          <div className="workshop-list-reg">
            {alreadyAddedWorkshops.map((workshop, index) => (
              <div
                key={`${workshop.WorkshopID}-${index}`}
                className="workshop-card"
              >
                <h3 className="workshop-title">{workshop.Topic}</h3>
                <p className="workshop-date">
                  <strong>{workshop.theDate}</strong>
                </p>
                <p className="workshop-price">
                  <strong>RS: {workshop.Price}</strong>
                </p>
                <p className="workshop-facilitators">
                  <strong>{workshop.Facilitators}</strong>
                </p>
                <p className="workshop-facilitators">
                  <strong>{workshop.Venue}</strong>
                </p>
                <p className="workshop-facilitators">
                  <strong>{workshop.duration}</strong>
                </p>
                <p className="workshop-focal">
                  <strong>{workshop.Focal}</strong>
                </p>
                <p className="workshop-status">Order Pending</p>
                <button
                  className="register-button"
                  onClick={() => handleProceedForAdded(workshop)}
                >
                  Pay Now
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Available Workshops Section */}
      <div className="section-container">
        <h2>Available Workshops</h2>
        <div className="workshop-list-reg">
          {filteredAvailableWorkshops.map((workshop, index) => (
            <div
              key={`${workshop.WorkshopID}-${index}`}
              className="workshop-card"
            >
              <h3 className="workshop-title">{workshop.Topic}</h3>
              <p className="workshop-date">
                <strong>{workshop.theDate}</strong>
              </p>
              <p className="workshop-price">
                <strong>RS: {workshop.Price}</strong>
              </p>
              <p className="workshop-facilitators">
                <strong>{workshop.Facilitators}</strong>
              </p>
              <p className="workshop-facilitators">
                <strong>{workshop.Venue}</strong>
              </p>
              <p className="workshop-facilitators">
                <strong>{workshop.duration}</strong>
              </p>
              <p className="workshop-focal">
                <strong>{workshop.Focal}</strong>
              </p>
              <button
                className="register-button"
                onClick={() =>
                  authToken
                    ? handleAddToCart(workshop)
                    : alert(
                        "Please sign in first to add this workshop to your cart."
                      )
                }
                disabled={workshop.seatUnavailable}
              >
                {workshop.seatUnavailable
                  ? "No Seats Available"
                  : "Add to Cart"}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default WorkshopRegistration;
